import { GridWallAccessEnum, GridWallStatusEnum, ProjectAssetEnum } from 'api/enums';
import { isString } from 'api/helpers';
import { immerable } from 'immer';
import { ClientModel, IClientModel } from './client.model';
import { IUserModel, UserModel } from './user.model';
import { PluginStatusEnum } from 'api/enums';

/* -------------------------------------------------------------------------------------------------
 * ICctvCameraGridData
 * -----------------------------------------------------------------------------------------------*/

export interface ICctvCameraGridData {
    _id: string;
    type: ProjectAssetEnum.CAMERA_CCTV;
    project: { _id: string; name: string };
    position: number;
    customText: string;
    hlsUrl: string;
    webrtcUrl: string;
    streamingUrl: string;
    frameUrl: string;
    cctvCamera: { _id: string; name: string };
    channel: string;
}

/* -------------------------------------------------------------------------------------------------
 * CctvCameraGridDataModel
 * -----------------------------------------------------------------------------------------------*/

export class CctvCameraGridDataModel {
    [immerable] = true;

    _id: string;
    type: ProjectAssetEnum.CAMERA_CCTV;
    project: { _id: string; name: string };
    position: number;
    customText: string;
    streamingUrl: string;
    hlsUrl: string;
    webrtcUrl: string;
    frameUrl: string;
    channel: string;
    cctvCamera: { _id: string; name: string };

    constructor(data: Partial<ICctvCameraGridData>) {
        this._id = data._id ?? '';
        this.type = data.type ?? null;
        this.project = data.project ?? null;
        this.position = data.position ?? null;
        this.customText = data.customText ?? '';
        this.streamingUrl = data.streamingUrl ?? '';
        this.hlsUrl = data.hlsUrl ?? '';
        this.webrtcUrl = data.webrtcUrl ?? '';
        this.frameUrl = data.frameUrl ?? '';
        this.cctvCamera = data.cctvCamera ?? null;
        this.channel = data.channel ?? null;
    }

    static fromObject(data: Partial<ICctvCameraGridData>): CctvCameraGridDataModel {
        const model = new CctvCameraGridDataModel(data);
        return model;
    }

    static fromObjectList(data: Partial<ICctvCameraGridData>[]): CctvCameraGridDataModel[] {
        return data.map((row) => CctvCameraGridDataModel.fromObject(row));
    }

    static cloneFrom(data: Partial<ICctvCameraGridData>): CctvCameraGridDataModel {
        const model = new CctvCameraGridDataModel(data);
        return model;
    }

    toObject(): ICctvCameraGridData {
        const data: Partial<ICctvCameraGridData> = {
            _id: this._id,
            type: this.type,
            project: this.project,
            position: this.position,
            customText: this.customText,
            hlsUrl: this.hlsUrl,
            webrtcUrl: this.webrtcUrl,
            streamingUrl: this.streamingUrl,
            frameUrl: this.frameUrl,
            cctvCamera: this.cctvCamera,
            channel: this.channel,
        };

        return data as ICctvCameraGridData;
    }
}

/* -------------------------------------------------------------------------------------------------
 * IGridDataModel
 * -----------------------------------------------------------------------------------------------*/

export interface IGridDataModel {
    _id: string;
    project: { _id: string; name: string };
    camera: { _id: string; name: string };
    type: ProjectAssetEnum.TIMELAPSE;
    position: number;
    customText: string;
    latestImage: {
        url: string;
        dateTime: string;
        date: string;
        time: string;
    };
}

/* -------------------------------------------------------------------------------------------------
 * GridDataModel
 * -----------------------------------------------------------------------------------------------*/
export class GridDataModel {
    [immerable] = true;

    _id: string;
    project: { _id: string; name: string };
    camera: { _id: string; name: string };
    type: ProjectAssetEnum.TIMELAPSE;
    position: number;
    latestImage: {
        url: string;
        dateTime: string;
        date: string;
        time: string;
    };
    customText: string;

    constructor(data: Partial<IGridDataModel>) {
        this._id = data._id ?? '';
        this.position = data.position ?? null;
        this.type = data.type ?? null;
        this.customText = data.customText ?? '';
        this.project = data.project ?? null;
        this.camera = data.camera ?? null;
        this.latestImage = data.latestImage ?? null;
    }

    static fromObject(data: Partial<IGridDataModel>): GridDataModel {
        const model = new GridDataModel(data);

        return model;
    }

    static fromObjectList(data: Partial<IGridDataModel>[]): GridDataModel[] {
        return data.map((row) => GridDataModel.fromObject(row));
    }

    static cloneFrom(data: Partial<IGridDataModel>): GridDataModel {
        const model = new GridDataModel(data);

        return model;
    }

    toObject(): IGridDataModel {
        const data: Partial<IGridDataModel> = {
            _id: this._id,
            project: this.project,
            camera: this.camera,
            type: this.type,
            position: this.position,
            customText: this.customText,
            latestImage: this.latestImage,
        };

        return data as IGridDataModel;
    }
}

/* -------------------------------------------------------------------------------------------------
 * IGridWallModel
 * -----------------------------------------------------------------------------------------------*/

export interface IGridWallModel {
    _id: string;
    name: string;
    user?: string | IUserModel;
    client: string | IClientModel;
    gridSize: { rows: number; cols: number };
    refreshInterval: number;
    token: string;
    access: GridWallAccessEnum;
    accessCode: string;
    gridData: (IGridDataModel | ICctvCameraGridData)[];
    status: GridWallStatusEnum;
    footerMessage: string;
    createdAt: Date;
    options: {
        showTimestamp: boolean;
        showCameraName: boolean;
        showProjectName: boolean;
        showCustomText: boolean;
        showZoomed: boolean;
    };
}

/* -------------------------------------------------------------------------------------------------
 * GridWallModel
 * -----------------------------------------------------------------------------------------------*/

export class GridWallModel {
    [immerable] = true;
    _id: string;
    name: string;
    user?: string | UserModel;
    client: string | ClientModel;
    gridSize: { rows: number; cols: number };
    refreshInterval: number;
    token: string;
    access: GridWallAccessEnum;
    accessCode: string;
    gridData: (GridDataModel | CctvCameraGridDataModel)[];
    status: GridWallStatusEnum;
    footerMessage: string;
    createdAt: Date;
    options: {
        showTimestamp: boolean;
        showCameraName: boolean;
        showProjectName: boolean;
        showCustomText: boolean;
        showZoomed: boolean;
    };

    constructor(data: Partial<IGridWallModel>) {
        this._id = data._id ?? '';
        this.name = data.name ?? '';

        this.gridSize = {
            rows: data.gridSize?.rows ?? null,
            cols: data.gridSize?.cols ?? null,
        };
        this.refreshInterval = data.refreshInterval ?? null;
        this.token = data.token ?? '';
        this.access = data.access ?? null;
        this.accessCode = data.accessCode ?? '';
        this.footerMessage = data.footerMessage ?? '';
        this.createdAt = data.createdAt ?? null;

        if (data.gridData) {
            this.gridData = data.gridData.map((row) => {
                if (row.type === ProjectAssetEnum.CAMERA_CCTV) {
                    return CctvCameraGridDataModel.fromObject(row as ICctvCameraGridData);
                }

                return GridDataModel.fromObject(row as IGridDataModel);
            });
        }
        this.status = data.status ?? null;

        this.options = {
            showTimestamp: data.options?.showTimestamp ?? null,
            showCameraName: data.options?.showCameraName ?? null,
            showProjectName: data.options?.showProjectName ?? null,
            showCustomText: data.options?.showCustomText ?? null,
            showZoomed: data.options?.showZoomed ?? null,
        };

        if (data.user) {
            if (isString(data.user)) {
                this.user = data.user as string;
            } else {
                this.user = UserModel.fromObject(data.user as IUserModel);
            }
        }

        if (data.client) {
            if (isString(data.client)) {
                this.client = data.client as string;
            } else {
                this.client = ClientModel.fromObject(data.client as IClientModel);
            }
        }
    }

    static fromObject(data: Partial<IGridWallModel>): GridWallModel {
        const model = new GridWallModel(data);

        return model;
    }

    static fromObjectList(data: Partial<IGridWallModel>[]): GridWallModel[] {
        return data.map((row) => GridWallModel.fromObject(row));
    }

    static cloneFrom(data: Partial<IGridWallModel>): GridWallModel {
        const model = new GridWallModel(data);

        return model;
    }

    toObject(): IGridWallModel {
        const data: Partial<IGridWallModel> = {
            _id: this._id,
            name: this.name,
            gridSize: this.gridSize,
            refreshInterval: this.refreshInterval,
            token: this.token,
            accessCode: this.accessCode,
            access: this.access,
            footerMessage: this.footerMessage,
            status: this.status,
            options: this.options,
            createdAt: this.createdAt,
        };

        if (this.gridData) {
            this.gridData.map((item) => {
                if (item instanceof GridDataModel) {
                    return item.toObject();
                }

                if (item instanceof CctvCameraGridDataModel) {
                    return item.toObject();
                }
                return item;
            });
        }

        if (this.user) {
            if (this.user instanceof UserModel) {
                data.user = this.user.toObject();
            } else {
                data.user = this.user;
            }
        }

        if (this.client) {
            if (this.client instanceof ClientModel) {
                data.client = this.client.toObject();
            } else {
                data.client = this.client;
            }
        }

        return data as IGridWallModel;
    }
}

/* -------------------------------------------------------------------------------------------------
 * IGridWallRefreshModel
 * -----------------------------------------------------------------------------------------------*/

export interface IGridWallRefreshModel {
    footerMessage: string;
    client: string | IClientModel;
    gridData: (IGridDataModel | ICctvCameraGridData)[];
    gridSize: { rows: number; cols: number };
    access: GridWallAccessEnum;
    options: {
        showTimestamp: boolean;
        showCameraName: boolean;
        showProjectName: boolean;
        showCustomText: boolean;
        showZoomed: boolean;
    };
}

/* -------------------------------------------------------------------------------------------------
 * GridWallRefreshModel
 * -----------------------------------------------------------------------------------------------*/

export class GridWallRefreshModel {
    [immerable] = true;
    client: string | ClientModel;
    gridData: (GridDataModel | CctvCameraGridDataModel)[];
    footerMessage: string;
    gridSize: { rows: number; cols: number };
    access: GridWallAccessEnum;
    options: {
        showTimestamp: boolean;
        showCameraName: boolean;
        showProjectName: boolean;
        showCustomText: boolean;
        showZoomed: boolean;
    };

    constructor(data: Partial<IGridWallRefreshModel>) {
        this.footerMessage = data.footerMessage;
        this.access = data.access;
        this.gridSize = {
            rows: data.gridSize?.rows ?? null,
            cols: data.gridSize?.cols ?? null,
        };
        this.options = {
            showTimestamp: data.options?.showTimestamp ?? null,
            showCameraName: data.options?.showCameraName ?? null,
            showProjectName: data.options?.showProjectName ?? null,
            showCustomText: data.options?.showCustomText ?? null,
            showZoomed: data.options?.showZoomed ?? null,
        };

        if (data.gridData) {
            this.gridData = data.gridData.map((row) => {
                if (row.type === ProjectAssetEnum.CAMERA_CCTV) {
                    return CctvCameraGridDataModel.fromObject(row as ICctvCameraGridData);
                }

                return GridDataModel.fromObject(row as IGridDataModel);
            });
        }

        if (data.client) {
            if (isString(data.client)) {
                this.client = data.client as string;
            } else {
                this.client = ClientModel.fromObject(data.client as IClientModel);
            }
        }
    }

    static fromObject(data: Partial<IGridWallRefreshModel>): GridWallRefreshModel {
        const model = new GridWallRefreshModel(data);

        return model;
    }

    static fromObjectList(data: Partial<IGridWallRefreshModel>[]): GridWallRefreshModel[] {
        return data.map((row) => GridWallRefreshModel.fromObject(row));
    }

    static cloneFrom(data: Partial<IGridWallRefreshModel>): GridWallRefreshModel {
        const model = new GridWallRefreshModel(data);

        return model;
    }

    toObject(): IGridWallRefreshModel {
        const data: Partial<IGridWallRefreshModel> = {
            footerMessage: this.footerMessage,
            access: this.access,
            gridSize: this.gridSize,
            options: this.options,
        };

        if (this.gridData) {
            this.gridData.map((item) => {
                if (item instanceof GridDataModel) {
                    return item.toObject();
                }

                if (item instanceof CctvCameraGridDataModel) {
                    return item.toObject();
                }
                return item;
            });
        }
        if (this.client) {
            if (this.client instanceof ClientModel) {
                data.client = this.client.toObject();
            } else {
                data.client = this.client;
            }
        }

        return data as IGridWallRefreshModel;
    }
}

/* -------------------------------------------------------------------------------------------------
 * IGridwallPluginModel
 * -----------------------------------------------------------------------------------------------*/

export interface IGridwallPluginModel {
    _id: string;
    status: PluginStatusEnum;
    details: {};
}

/* -------------------------------------------------------------------------------------------------
 * GridwallPluginModel
 * -----------------------------------------------------------------------------------------------*/

export class GridwallPluginModel {
    [immerable] = true;

    _id: string;
    status: PluginStatusEnum;
    details: {};

    constructor(data: Partial<IGridwallPluginModel>) {
        this._id = data._id || '';
        this.status = data.status ?? null;
        this.details = {};
    }

    static fromObject(data: Partial<IGridwallPluginModel>): GridwallPluginModel {
        const model = new GridwallPluginModel(data);

        return model;
    }

    static cloneFrom(data: Partial<IGridwallPluginModel>): GridwallPluginModel {
        const model = new GridwallPluginModel(data);

        return model;
    }

    toObject(): IGridwallPluginModel {
        const data: IGridwallPluginModel = {
            _id: this._id,
            status: this.status,
            details: {},
        };

        return data;
    }
}

/* -------------------------------------------------------------------------------------------------
 * IGridwallRequestFileModel
 * -----------------------------------------------------------------------------------------------*/

export interface IGridwallRequestFileModel {
    _id: string;
    name: string;
    url: string;
}

/* -------------------------------------------------------------------------------------------------
 * GridwallRequestFileModel
 * -----------------------------------------------------------------------------------------------*/
export class GridwallRequestFileModel {
    [immerable] = true;

    _id: string;
    name: string;
    url: string;

    constructor(data: Partial<IGridwallRequestFileModel>) {
        this._id = data._id ?? '';
        this.name = data.url ?? '';
        this.url = data.url ?? '';
    }

    static fromObject(data: Partial<IGridwallRequestFileModel>): GridwallRequestFileModel {
        const model = new GridwallRequestFileModel(data);

        return model;
    }

    static fromObjectList(data: Partial<IGridwallRequestFileModel>[]): GridwallRequestFileModel[] {
        return data.map((row) => GridwallRequestFileModel.fromObject(row));
    }

    static cloneFrom(data: Partial<IGridwallRequestFileModel>): GridwallRequestFileModel {
        const model = new GridwallRequestFileModel(data);

        return model;
    }

    toObject(): IGridwallRequestFileModel {
        const data: IGridwallRequestFileModel = {
            _id: this._id,
            name: this.name,
            url: this.url || null,
        };

        return data;
    }
}

/* -------------------------------------------------------------------------------------------------
 * IGridwallRequestModel
 * -----------------------------------------------------------------------------------------------*/

export interface IGridwallRequestModel {
    _id: string;
    name: string;
    user: string | IUserModel;
    client: string | IClientModel;
    remarks: string;
    files: IGridwallRequestFileModel[];
    createdAt: string;
    updatedAt: string;
}

/* -------------------------------------------------------------------------------------------------
 * GridwallRequestModel
 * -----------------------------------------------------------------------------------------------*/
export class GridwallRequestModel {
    [immerable] = true;
    _id: string;
    name: string;
    user: string | UserModel;
    client: string | ClientModel;
    remarks: string;
    files: IGridwallRequestFileModel[];
    createdAt: string;
    updatedAt: string;

    constructor(data: Partial<IGridwallRequestModel>) {
        this._id = data._id ?? '';
        this.name = data.name ?? '';
        this.remarks = data.remarks ?? '';
        this.createdAt = data.createdAt ?? '';
        this.updatedAt = data.updatedAt ?? '';

        if (data.user) {
            if (isString(data.user)) {
                this.user = data.user as string;
            } else {
                this.user = UserModel.fromObject(data.user as IUserModel);
            }
        }

        if (data.client) {
            if (isString(data.client)) {
                this.client = data.client as string;
            } else {
                this.client = ClientModel.fromObject(data.client as IClientModel);
            }
        }

        if (data.files) {
            this.files = data.files.map((row) => GridwallRequestFileModel.fromObject(row));
        }
    }

    static fromObject(data: Partial<IGridwallRequestModel>): GridwallRequestModel {
        const model = new GridwallRequestModel(data);

        return model;
    }

    static fromObjectList(data: Partial<IGridwallRequestModel>[]): GridwallRequestModel[] {
        return data.map((row) => GridwallRequestModel.fromObject(row));
    }

    static cloneFrom(data: Partial<IGridwallRequestModel>): GridwallRequestModel {
        const model = new GridwallRequestModel(data);

        return model;
    }

    toObject(): IGridwallRequestModel {
        const data: Partial<IGridwallRequestModel> = {
            _id: this._id,
            name: this.name,
            remarks: this.remarks,
            createdAt: this.createdAt,
            updatedAt: this.updatedAt,
        };

        if (this.files) {
            this.files.map((item) => {
                if (item instanceof GridwallRequestFileModel) {
                    return item.toObject();
                }
                return item;
            });
        }

        if (this.client) {
            if (this.client instanceof ClientModel) {
                data.client = this.client.toObject();
            } else {
                data.client = this.client;
            }
        }

        if (this.user) {
            if (this.user instanceof UserModel) {
                data.user = this.user.toObject();
            } else {
                data.user = this.user;
            }
        }

        return data as IGridwallRequestModel;
    }
}
